export interface EventRow {
  ts: string;
  door: string;
  site: string;
  kind: 'ok' | 'warn' | 'info' | 'danger';
  label: string;
}

export interface ContactDraft {
  subject: string;
  name: string;
  email: string;
  message: string;
}

export const CONTACT_EMAIL = 'hello@cdvi-edge.com';

export const EVENT_POOL: Omit<EventRow, 'ts'>[] = [
  { door: 'North Lobby', site: 'NYC / 14 Hudson', kind: 'ok', label: 'schedule unlock' },
  { door: 'Suite 1402', site: 'NYC / 14 Hudson', kind: 'info', label: 'mobile key issued' },
  { door: 'Pool Deck', site: 'MIA / Brickell A', kind: 'ok', label: 'access granted' },
  { door: 'Service Entry', site: 'LAX / Sunset West', kind: 'warn', label: 'held open 22s' },
  { door: 'Garage P2', site: 'NYC / 14 Hudson', kind: 'info', label: 'vendor window' },
  { door: 'Suite 0911', site: 'MIA / Brickell A', kind: 'info', label: 'guest check-in' },
  { door: 'Roof Access', site: 'LAX / Sunset West', kind: 'danger', label: 'revoked denied' },
  { door: 'Gym Entry', site: 'SEA / Pier 70', kind: 'ok', label: 'resident badge' },
  { door: 'Loading Dock', site: 'SEA / Pier 70', kind: 'ok', label: 'schedule applied' },
  { door: 'Staff Corridor', site: 'LAX / Sunset West', kind: 'ok', label: 'housekeeping pass' },
];

export const HERO_STATS = [
  { label: 'p50 relay latency', value: 142, suffix: 'ms', note: 'door event to cloud console' },
  { label: 'operator servers', value: 0, suffix: '', note: 'no VPN, no exposed ports' },
  { label: 'audit trail', value: 100, suffix: '%', note: 'tenant-scoped by query' },
];

export const PROOF_POINTS = [
  { k: 'Legacy ATRIUM panels', v: 'cloud-ready', note: 'bridge through the on-prem relay' },
  { k: 'Sites per tenant', v: 'unlimited', note: 'one portfolio console' },
  { k: 'Install path', v: '< 1 day', note: 'keep cards, schedules, doors' },
  { k: 'Runtime', v: 'Cloudflare + Neon', note: 'edge API with scoped data access' },
];

export const FEATURE_CARDS = [
  {
    key: 'relay',
    title: 'Relay-native, not VPN-shaped',
    text: 'The local relay handles ATRIUM crypto, then streams clean cloud events without opening the controller to the internet.',
  },
  {
    key: 'portfolio',
    title: 'Built for many buildings',
    text: 'Tenant-aware queries, multi-site dashboards, alerts, cardholders, schedules, and controller health in one surface.',
  },
  {
    key: 'operator',
    title: 'A control room in your pocket',
    text: 'PWA install, push alerts, mobile-friendly door actions, and offline-tolerant workflows for field teams.',
  },
  {
    key: 'hotel',
    title: 'Hospitality mode ready',
    text: 'A foundation for PMS-driven mobile keys, check-in provisioning, checkout revocation, and guest-safe audit trails.',
  },
];

export const COMMAND_STEPS = [
  { id: '01', title: 'Relay pairs', detail: 'A small on-prem process logs into the panel and translates SDK XML into typed events.' },
  { id: '02', title: 'Cloud scopes', detail: 'Every event and command is written against tenant-scoped database access.' },
  { id: '03', title: 'Operators act', detail: 'Teams unlock, audit, investigate, and respond from web or installed PWA.' },
];

export function relTime(secondsAgo: number): string {
  if (secondsAgo < 60) return `${secondsAgo}s`;
  const minutes = Math.floor(secondsAgo / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
}

export function makeInitialRows(): EventRow[] {
  return EVENT_POOL.slice(0, 6).map((event, index) => ({
    ...event,
    ts: relTime(2 + index * 7),
  }));
}

export function buildContactMailto({ subject, name, email, message }: ContactDraft): string {
  const lines: string[] = [];
  if (name.trim()) lines.push(`Name: ${name.trim()}`);
  if (email.trim()) lines.push(`Email: ${email.trim()}`);
  lines.push('', message.trim());

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    lines.join('\n'),
  )}`;
}
